// WHAT: Card component for displaying total tracked time for selected period
// WHY:  Provides quick overview of total time spent in selected filter period
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: useFilter hook, formatMinutes utility, core.invoke
// RISKS:
//   - NOTE: Backend response structure may not match expected format
//   - NOTE: Error handling may not be user-friendly
//   - NOTE: Loading state may flash on filter changes

import { core } from "@tauri-apps/api";
import { useState, useEffect } from "react";

import { Card } from "@/components/ui";

import { formatMinutes } from "../../utils/format";
import { useFilter } from "./FilterContextProvider.shared";

/**
 * WHAT:
 *   Card component that displays total tracked time for the selected filter
 *   period with formatted display.
 *
 * WHY:
 *   Provides quick overview of total time investment in selected period.
 *
 * BEHAVIOR:
 *   - Fetches aggregated summary from backend
 *   - Extracts total minutes from response
 *   - Formats and displays time
 *   - Updates when filter changes
 *   - Shows loading and error states
 *
 * RISKS:
 *   - NOTE: Backend response structure may not match expected format
 *   - NOTE: Error handling may not be user-friendly
 *   - NOTE: Loading state may flash on filter changes
 *
 * @returns {JSX.Element} The total time card component
 *
 */
export const TotalTimeCard = () => {
  const { dateFilter, period } = useFilter();

  const [totalMinutes, setTotalMinutes] = useState<null | number>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await core.invoke<{ total_minutes: number }>("get_aggregated_summary", {
          filter: dateFilter,
        });
        setTotalMinutes(result.total_minutes);
      } catch {
        // NOTE: Error loading data
        setError("Fehler beim Laden der Daten");
        setTotalMinutes(0);
      } finally {
        setLoading(false);
      }
    };

    void fetchData();
  }, [dateFilter]); // NOTE: Re-runs when global filter changes

  return (
    <Card
      className="bg-slate-800/30 border-slate-600/50 shadow-tracq-card"
      padding="lg"
      variant="elevated"
    >
      <div className="space-y-1">
        <div className="text-sm text-slate-400">Total Time ({period})</div>

        <div className="text-3xl font-bold">
          {(() => {
            if (loading) {
              return <span className="text-slate-500 animate-pulse">Loading…</span>;
            }
            if (error) {
              return <span className="text-red-400 text-lg">Error</span>;
            }
            return <span className="text-white">{formatMinutes(totalMinutes)}</span>;
          })()}
        </div>

        {error ? <div className="text-xs text-red-400 mt-1">{error}</div> : null}
      </div>
    </Card>
  );
};
